// --- START PATCH: treat /admin and /account as protected; emit auth events for cart refresh ---
import React, { useState, useEffect, useCallback } from "react";
import { User } from "./types";
import axiosInstance, { setIsLoggingOut, AUTH_EVENTS } from "./axiosWithInterceptor";
import { AuthContext } from "./AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import i18n from "@/i18n/config";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Include all route prefixes that require an authenticated session.
  // This ensures we always bootstrap user state on hard refresh.
  const protectedPaths = ["/admin", "/account", "/dashboard", "/profile", "/settings", "/inventory"];

  const isProtected = protectedPaths.some((path) =>
    location.pathname.startsWith(path)
  );

  // --- START EDIT: Stabilize applyPreferredLanguage with useCallback ---
  // Helper to apply preferred language if present
  const applyPreferredLanguage = useCallback(async (userData: User) => {
    if (userData.preferred_language) {
      await i18n.changeLanguage(userData.preferred_language);
      localStorage.setItem('i18nextLng', userData.preferred_language);
    }
  }, []);
  // --- END EDIT ---

  // --- START EDIT: Stabilize refreshToken with useCallback ---
  const refreshToken = useCallback(async () => {
    try {
      await axiosInstance.post("/refresh-token", {}, { withCredentials: true });
      const response = await axiosInstance.get("/me", {
        withCredentials: true,
      });
      setUser(response.data);
      await applyPreferredLanguage(response.data);
    } catch (error) {
      console.error("Refresh token failed", error);
      setUser(null);
    }
  }, [applyPreferredLanguage]);
  // --- END EDIT ---

  const login = async (userData: User) => {
    setUser(userData);
    await applyPreferredLanguage(userData);
    // --- START: Emit auth:loggedIn event for CartProvider ---
    window.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGGED_IN));
    // --- END: Emit auth:loggedIn event ---
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      await axiosInstance.post("/logout", {}, { withCredentials: true });
    } catch {
      // ignore error
    }
    setUser(null);
    setIsLoggingOut(false);
    // --- START: Emit auth:loggedOut event for CartProvider ---
    // Note: guest_session_id is NOT cleared on logout (per requirements)
    window.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGGED_OUT));
    // --- END: Emit auth:loggedOut event ---
    navigate("/");
  };

  // --- START EDIT: Extract and stabilize fetchUser with useCallback ---
  const fetchUser = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/me", {
        withCredentials: true,
      });
      setUser(response.data);
      await applyPreferredLanguage(response.data);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        await refreshToken();
      } else {
        console.error("Failed to fetch user data:", error);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [applyPreferredLanguage, refreshToken]);
  // --- END EDIT ---

  // --- START EDIT: Update useEffect dependencies to include fetchUser ---
  useEffect(() => {
    if (!isProtected) {
      setLoading(false);
      return;
    }

    fetchUser();
  }, [location.pathname, isProtected, fetchUser]);
  // --- END EDIT ---

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refreshToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};
// --- END PATCH: treat /admin and /account as protected; emit auth events for cart refresh ---